import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Camera, Edit, Loader2 } from "lucide-react";
import { generateAvatarPlaceholder } from "@/lib/utils";
import logoImage from "@assets/legacyloop-white-trans.png";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Sorted by sport name, then gender (Men's before Women's); "Other" kept last as the catch-all.
// Keep this list in sync with the sport options in WelcomeSetup.tsx and mobile/src/app/create-athlete.tsx.
const SPORT_OPTIONS = [
  { value: "Baseball", label: "Baseball" },
  { value: "Men's Basketball", label: "Men's Basketball" },
  { value: "Women's Basketball", label: "Women's Basketball" },
  { value: "Cross Country", label: "Cross Country" },
  { value: "Football", label: "Football" },
  { value: "Men's Golf", label: "Men's Golf" },
  { value: "Women's Golf", label: "Women's Golf" },
  { value: "Men's Soccer", label: "Men's Soccer" },
  { value: "Women's Soccer", label: "Women's Soccer" },
  { value: "Softball", label: "Softball" },
  { value: "Tennis", label: "Tennis" },
  { value: "Track and Field", label: "Track and Field" },
  { value: "Men's Volleyball", label: "Men's Volleyball" },
  { value: "Women's Volleyball", label: "Women's Volleyball" },
  { value: "Other", label: "Other" },
];

const AthleteProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [athleteForm, setAthleteForm] = useState({
    name: `${(user as any)?.firstName || ""} ${(user as any)?.lastName || ""}`.trim(),
    sports: "",
    position: "",
    graduationYear: new Date().getFullYear() + 4,
    gpa: 0.0,
    school: "",
    bio: ""
  });
  const [editForm, setEditForm] = useState<any>(null);

  const { athlete, isLoading: isLoadingAthlete } = useActiveAthlete();

  // ---- Create ----
  const createAthleteMutation = useMutation({
    mutationFn: async (athleteData: typeof athleteForm) => {
      const response = await apiRequest("POST", "/api/athletes", {
        ...athleteData,
        userId: (user as any)?.id,
      });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Failed to create athlete profile (${response.status})`);
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Profile Created",
        description: "Your athlete profile has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes"] });
      setShowCreateForm(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create athlete profile",
        variant: "destructive",
      });
    },
  });

  // ---- Edit ----
  const openEdit = () => {
    setEditForm({
      name: athlete?.name ?? "",
      sports: athlete?.sports ?? "",
      position: athlete?.position ?? "",
      graduationYear: athlete?.graduationYear ?? new Date().getFullYear() + 4,
      gpa: athlete?.gpa ?? "",
      school: athlete?.school ?? "",
      bio: athlete?.bio ?? "",
    });
    setShowEditForm(true);
  };

  const updateAthleteMutation = useMutation({
    mutationFn: async (data: typeof editForm) => {
      const res = await apiRequest("PUT", `/api/athletes/${athlete.id}`, data);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to update profile (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Profile updated", description: "Your athlete profile has been saved." });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes"] });
      setShowEditForm(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleCreateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    createAthleteMutation.mutate(athleteForm);
  };

  const handleEditProfile = (e: React.FormEvent) => {
    e.preventDefault();
    updateAthleteMutation.mutate(editForm);
  };

  // Resize and center-crop any image to a square JPEG at targetPx × targetPx.
  // Uploading a pre-cropped square eliminates stretching and ensures crisp
  // rendering inside the round avatar at any pixel density.
  function cropToSquareJpeg(file: File, targetPx = 400): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement("canvas");
        canvas.width = targetPx;
        canvas.height = targetPx;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas unavailable"));
        // Pick the largest centered square from the source image
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, targetPx, targetPx);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
          "image/jpeg",
          0.92,
        );
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("Image load failed")); };
      img.src = objectUrl;
    });
  }

  // Upload a new profile photo: crop → sign → PUT to S3 → PATCH /api/user → refresh user cache
  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_MB = 10;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: "File too large", description: `Profile photos must be under ${MAX_MB} MB.`, variant: "destructive" });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({ title: "Images only", description: "Please select a JPG, PNG, or WebP file.", variant: "destructive" });
      return;
    }

    setAvatarUploading(true);
    try {
      // 1. Resize and center-crop to a 400×400 JPEG before uploading
      const blob = await cropToSquareJpeg(file, 400);

      // 2. Get presigned PUT URL from backend
      const signRes = await apiRequest("/uploads/sign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contentType: "image/jpeg", ext: "jpg", bytes: blob.size }),
      });
      if (!signRes.ok) throw new Error("Could not get upload URL.");
      const { url, key } = await signRes.json();

      // 3. PUT the cropped blob directly to S3
      const s3Res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        body: blob,
      });
      if (!s3Res.ok) throw new Error("Upload to storage failed.");

      // 4. Save the S3 key to the user's profile
      const patchRes = await apiRequest("/user", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profileImageKey: key }),
      });
      if (!patchRes.ok) throw new Error("Could not save profile photo.");

      // 5. Refresh the user cache so the new photo appears immediately
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({ title: "Profile photo updated", description: "Your new photo is live." });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  }

  if (isLoadingAthlete) {
    return <AthleteProfileSkeleton />;
  }

  // ---- No profile yet ----
  if (!athlete) {
    return (
      <>
        <Card className="mb-8 shadow-md border border-[#E0A458]/40 bg-[#0D1B2A]">
          <CardContent className="p-8">
            <div className="text-center p-4 flex flex-col items-center gap-3">
              <img src={logoImage} alt="LegacyLoop" className="h-40 w-auto" />
              <h2 className="font-montserrat font-bold text-2xl text-white">Welcome to LegacyLoop!</h2>
              <p className="text-slate-300 max-w-md">
                Create your athlete profile to get started tracking your athletic journey.
              </p>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="mt-2 bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold px-8"
              >
                Create Athlete Profile
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0D1B2A] border border-[#E0A458]/30 text-white">
            <DialogHeader>
              <DialogTitle className="text-white font-montserrat">Create Athlete Profile</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProfile} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                  <Input
                    id="name"
                    value={athleteForm.name}
                    onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sports" className="text-slate-300">Sport</Label>
                  <Select
                    value={athleteForm.sports}
                    onValueChange={(v) => setAthleteForm({ ...athleteForm, sports: v })}
                    required
                  >
                    <SelectTrigger id="sports" className="bg-[#1B263B] border-[#415A77] text-white">
                      <SelectValue placeholder="Select a sport" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1B263B] border-[#415A77] text-white">
                      {SPORT_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="focus:bg-[#E0A458]/20 focus:text-white">
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position" className="text-slate-300">Position</Label>
                  <Input
                    id="position"
                    placeholder="e.g., Pitcher, Forward"
                    value={athleteForm.position}
                    onChange={(e) => setAthleteForm({ ...athleteForm, position: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school" className="text-slate-300">School Name</Label>
                  <Input
                    id="school"
                    value={athleteForm.school}
                    onChange={(e) => setAthleteForm({ ...athleteForm, school: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="graduationYear" className="text-slate-300">Graduation Year</Label>
                  <Input
                    id="graduationYear"
                    type="number"
                    min={new Date().getFullYear()}
                    max={new Date().getFullYear() + 10}
                    value={athleteForm.graduationYear}
                    onChange={(e) => setAthleteForm({ ...athleteForm, graduationYear: parseInt(e.target.value) })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gpa" className="text-slate-300">GPA</Label>
                  <Input
                    id="gpa"
                    type="number"
                    step="0.01"
                    min="0"
                    max="5.0"
                    placeholder="e.g., 3.5"
                    value={athleteForm.gpa}
                    onChange={(e) => setAthleteForm({ ...athleteForm, gpa: parseFloat(e.target.value) })}
                    className="bg-[#1B263B] border-[#415A77] text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-slate-300">Biography</Label>
                <textarea
                  id="bio"
                  className="flex min-h-24 w-full rounded-md border border-[#415A77] bg-[#1B263B] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#E0A458]"
                  placeholder="Brief description about the athlete"
                  value={athleteForm.bio}
                  onChange={(e) => setAthleteForm({ ...athleteForm, bio: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}
                  className="border-[#415A77] text-slate-300 hover:bg-[#1B263B]">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
                  disabled={createAthleteMutation.isPending}>
                  {createAthleteMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
                  ) : "Create Profile"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ---- Has profile ----
  return (
    <section className="mb-8">
      <Card className="overflow-hidden shadow-md border border-[#E0A458]/20 bg-[#0D1B2A]">
        {/* Banner */}
        <div className="h-32 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] border-b border-[#E0A458]/20 relative">
          <div className="absolute bottom-0 left-0 transform translate-y-1/2 ml-6 flex items-end">
            {/* Hidden file input — triggered by clicking the avatar */}
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              className="relative h-24 w-24 group rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A458]"
              title="Change profile photo"
            >
              <div className="h-full w-full bg-[#1B263B] border-2 border-[#E0A458]/60 rounded-full p-1 shadow-lg">
                <Avatar className="h-full w-full">
                  <AvatarImage src={(user as any)?.profileImageUrl} alt={athlete.name} className="object-cover" />
                  <AvatarFallback className="text-lg bg-[#415A77] text-white font-montserrat font-bold">
                    {generateAvatarPlaceholder(athlete.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
              {/* Camera overlay — visible on hover or while uploading */}
              <div className={`absolute inset-0 rounded-full flex items-center justify-center transition-opacity ${avatarUploading ? "opacity-100 bg-black/60" : "opacity-0 group-hover:opacity-100 bg-black/50"}`}>
                {avatarUploading
                  ? <Loader2 className="h-6 w-6 text-white animate-spin" />
                  : <Camera className="h-6 w-6 text-white" />
                }
              </div>
            </button>
          </div>
          <div className="absolute top-4 right-4">
            <Button
              size="icon"
              variant="ghost"
              onClick={openEdit}
              className="bg-[#E0A458]/20 backdrop-blur-sm text-[#E0A458] hover:bg-[#E0A458]/40 border border-[#E0A458]/40"
              title="Edit profile"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Profile details */}
        <div className="pt-16 pb-6 px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
            <div>
              <h2 className="font-montserrat font-bold text-2xl text-white">{athlete.name}</h2>
              <p className="text-[#E0A458]/90 font-medium mt-0.5">
                {athlete.sports}{athlete.position ? ` · ${athlete.position}` : ""}
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
              {athlete.graduationYear && (
                <Badge className="bg-[#E0A458]/15 text-[#E0A458] border border-[#E0A458]/40 px-3 py-1 font-medium">
                  Class of {athlete.graduationYear}
                </Badge>
              )}
              {athlete.gpa != null && (
                <Badge className="bg-[#1B263B] text-slate-200 border border-[#415A77] px-3 py-1 font-medium">
                  GPA: {Number(athlete.gpa).toFixed(2)}
                </Badge>
              )}
              {athlete.school && (
                <Badge className="bg-[#1B263B] text-slate-200 border border-[#415A77] px-3 py-1 font-medium">
                  {athlete.school}
                </Badge>
              )}
            </div>
          </div>

        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0D1B2A] border border-[#E0A458]/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-montserrat">Edit Athlete Profile</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleEditProfile} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="edit-name" className="text-slate-300">Full Name</Label>
                  <Input id="edit-name" value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sports" className="text-slate-300">Sport</Label>
                  <Select
                    value={SPORT_OPTIONS.some((s) => s.value === editForm.sports) ? editForm.sports : ""}
                    onValueChange={(v) => setEditForm({ ...editForm, sports: v })}
                  >
                    <SelectTrigger id="edit-sports" className="bg-[#1B263B] border-[#415A77] text-white">
                      <SelectValue placeholder="Select a sport" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1B263B] border-[#415A77] text-white">
                      {SPORT_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="focus:bg-[#E0A458]/20 focus:text-white">
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-position" className="text-slate-300">Position</Label>
                  <Input id="edit-position" value={editForm.position}
                    onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-school" className="text-slate-300">School</Label>
                  <Input id="edit-school" value={editForm.school}
                    onChange={(e) => setEditForm({ ...editForm, school: e.target.value })}
                    className="bg-[#1B263B] border-[#415A77] text-white" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-year" className="text-slate-300">Graduation Year</Label>
                  <Input id="edit-year" type="number"
                    min={new Date().getFullYear()} max={new Date().getFullYear() + 10}
                    value={editForm.graduationYear}
                    onChange={(e) => setEditForm({ ...editForm, graduationYear: parseInt(e.target.value) })}
                    className="bg-[#1B263B] border-[#415A77] text-white" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-gpa" className="text-slate-300">GPA</Label>
                  <Input id="edit-gpa" type="number" step="0.01" min="0" max="5.0"
                    value={editForm.gpa}
                    onChange={(e) => setEditForm({ ...editForm, gpa: parseFloat(e.target.value) })}
                    className="bg-[#1B263B] border-[#415A77] text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-bio" className="text-slate-300">Biography</Label>
                <textarea id="edit-bio"
                  className="flex min-h-24 w-full rounded-md border border-[#415A77] bg-[#1B263B] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#E0A458]"
                  placeholder="Brief description about the athlete"
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} />
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditForm(false)}
                  className="border-[#415A77] text-slate-300 hover:bg-[#1B263B]">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
                  disabled={updateAthleteMutation.isPending}>
                  {updateAthleteMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                  ) : "Save Changes"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const AthleteProfileSkeleton = () => (
  <section className="mb-8">
    <Card className="overflow-hidden shadow-md border border-[#E0A458]/20 bg-[#0D1B2A]">
      <div className="h-32 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] relative">
        <div className="absolute bottom-0 left-0 transform translate-y-1/2 ml-6">
          <Skeleton className="h-24 w-24 rounded-full bg-[#1B263B]" />
        </div>
      </div>
      <div className="pt-16 pb-6 px-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <div>
            <Skeleton className="h-8 w-48 mb-2 bg-[#1B263B]" />
            <Skeleton className="h-4 w-32 bg-[#1B263B]" />
          </div>
          <div className="mt-4 md:mt-0">
            <Skeleton className="h-6 w-24 rounded-full inline-block bg-[#1B263B]" />
            <Skeleton className="h-6 w-24 rounded-full inline-block ml-2 bg-[#1B263B]" />
          </div>
        </div>
      </div>
    </Card>
  </section>
);

export default AthleteProfile;
